import React from 'react'
import { useLoaderData } from 'react-router-dom'
import type { LoaderFunction } from 'react-router-dom'
import { DateWithUTCTooltip } from '../common'
import { authenticatedFetchData, API_HOST, BROWSER_TIMEZONE } from '../../lib'
import { logError } from '@tomra/datadog-browser-logging'
import { PayoutsTable } from '../common/PayoutsTable'
import { Notes } from '../common/notes/Notes'
import { Card } from '@tomra/design-system-react/card'

export const consumerSessionLoader: LoaderFunction = async ({ params }) => {
  try {
    const { dpuid } = params

    const consumerSessionInfo = await authenticatedFetchData(
      `${API_HOST}/mytomra/v1.0/hotlinetool/cs?dpuid=${encodeURIComponent(dpuid || '')}`
    ).run()
    return { consumerSessionInfo }
  } catch (error: any) {
    logError(new Error('Failed to fetch CS info'), error)
    return { error }
  }
}

type ConsumerSessionInfo = {
  consumerSession: any
  payouts: any[]
}

export const ConsumerSessionPage = () => {
  const { consumerSessionInfo, error } = useLoaderData() as { consumerSessionInfo?: ConsumerSessionInfo; error?: any }

  if (error || !consumerSessionInfo) {
    return (
      <div>
        <h1 className="sr-only">Consumer session</h1>
        <div className="rounded-lg border-l-[3px] border-rose-500 bg-white p-4 text-left shadow-md dark:bg-gray-900 centerAbsolute">
          Whoopsie! Failed to find consumer session info. Please try again.
        </div>
      </div>
    )
  }

  const { consumerSession, payouts } = consumerSessionInfo
  const { created, dpuid, itemCount, refund, currency, rvmSerial, latestStatus, voucherBarcode, errorCategory } =
    consumerSession

  return (
    <div className="p-6">
      <h1 className="text-xl mb-4">Consumer session</h1>

      <div className="flex">
        <div className="mr-2 w-1/4">
          <Card.Root className="mb-6">
            <Card.Content>
              <dl className="mb-4">
                <dt id="dpuid" className="mb-2 text-gray-600">
                  DPUID
                </dt>
                <dd aria-labelledby="dpuid">{dpuid}</dd>
              </dl>

              <dl className="mb-4">
                <dt id="date" className="mb-2 text-gray-600">
                  Date ({BROWSER_TIMEZONE})
                </dt>
                <dd aria-labelledby="date">
                  <DateWithUTCTooltip date={created} />
                </dd>
              </dl>

              <dl className="mb-4">
                <dt id="rvmSerial" className="mb-2 text-gray-600">
                  RVM Serial
                </dt>
                <dd aria-labelledby="rvmSerial">{rvmSerial}</dd>
              </dl>

              <dl className="mb-4">
                <dt id="itemCount" className="mb-2 text-gray-600">
                  Item count
                </dt>
                <dd aria-labelledby="itemCount">{itemCount}</dd>
              </dl>

              <dl className="mb-4">
                <dt id="refund" className="mb-2 text-gray-600">
                  Refund
                </dt>
                <dd aria-labelledby="refund">
                  {refund && refund.toFixed(2)} {currency}
                </dd>
              </dl>

              {voucherBarcode && (
                <dl className="mb-4">
                  <dt id="voucherBarcode" className="mb-2 text-gray-600">
                    Voucher barcode
                  </dt>
                  <dd aria-labelledby="voucherBarcode">{voucherBarcode}</dd>
                </dl>
              )}

              <dl className="mb-4">
                <dt id="status" className="mb-2 text-gray-600">
                  Latest status
                </dt>
                <dd aria-labelledby="status">
                  {latestStatus}
                  {errorCategory && <span> ({errorCategory}))</span>}
                </dd>
              </dl>
            </Card.Content>
          </Card.Root>

          <Notes sourceId={dpuid} />
        </div>

        <div className="ml-2 w-3/4">
          <Card.Root>
            <Card.Header>
              <h2 className="text-lg font-medium">Payouts</h2>
            </Card.Header>

            <PayoutsTable payouts={payouts} />
          </Card.Root>
        </div>
      </div>
    </div>
  )
}
